import { type ListingCardProps } from '@/types';
import { MapPin, Star } from 'lucide-react';
import React from 'react';

export const ListingCard: React.FC<ListingCardProps> = ({
    id: _id,
    title,
    slug: _slug,
    category,
    categorySlug,
    location,
    averageRating,
    reviewCount,
    summary,
    imageUrl,
    isFavorited: _isFavorited = false,
    isVerified: _isVerified = false,
    isFeatured = false,
    onFavorite: _onFavorite,
    onViewProfile,
}) => {
    const hasCategory = Boolean(category?.trim());
    const hasLocation = Boolean(location?.trim());
    const summaryText = summary?.trim() ?? '';

    return (
        <div className="group relative flex h-full w-full flex-col overflow-hidden rounded-lg bg-white shadow-sm transition-all hover:shadow-md">
            {/* Header with Logo */}
            <div className="relative flex h-36 items-center justify-center bg-gray-200 p-6">
                {imageUrl ? (
                    <img src={imageUrl} alt={title} className="max-h-full max-w-full object-contain" />
                ) : (
                    <div className="text-3xl font-bold text-white">{title.charAt(0)}</div>
                )}

                {/* Badges */}
                <div className="absolute top-3 left-3 flex flex-row items-center justify-center gap-2">
                    {/* {isFeatured && ( */}
                    <Star className="h-3 w-3 fill-current text-yellow-400" />
                    {/* )} */}
                    {/* {isVerified && ( */}
                    <div className="size-4 rounded-full bg-blue-700 text-center text-xs font-semibold text-white">✓</div>
                    {/* )} */}
                </div>
            </div>

            {/* Content */}
            <div className="flex flex-1 flex-col px-4 pt-2 pb-4">
                {/* Title */}
                <h3
                    className={
                        isFeatured
                            ? 'line-clamp-2 min-h-[2lh] text-lg leading-snug font-semibold text-gray-900'
                            : 'line-clamp-1 text-lg font-semibold text-gray-900'
                    }
                >
                    {title}
                </h3>

                {/* Category Badge */}
                {isFeatured ? (
                    hasCategory && categorySlug ? (
                        <a
                            href={`/search?type=empresas&category=${categorySlug}`}
                            onClick={(e) => e.stopPropagation()}
                            className="mb-0.5 line-clamp-2 block min-h-[2lh] max-w-full py-1 text-xs leading-tight font-bold text-cyan-500 transition-colors"
                        >
                            {category}
                        </a>
                    ) : (
                        <span className="mb-0.5 line-clamp-2 block min-h-[2lh] max-w-full py-1 text-xs leading-tight font-bold text-cyan-500">
                            {hasCategory ? category : '\u00A0'}
                        </span>
                    )
                ) : (
                    category && (
                        <a
                            href={categorySlug ? `/search?type=empresas&category=${categorySlug}` : '#'}
                            onClick={(e) => e.stopPropagation()}
                            className="mb-0.5 inline-flex w-fit items-center py-1 text-xs font-bold text-cyan-500 transition-colors"
                        >
                            {category}
                        </a>
                    )
                )}

                {/* Location */}
                {(isFeatured || hasLocation) && (
                    <div className="mb-2 flex items-center gap-1 text-sm text-gray-600">
                        <MapPin className="h-4 w-4 shrink-0" />
                        <span className="line-clamp-1">{hasLocation ? location : '-'}</span>
                    </div>
                )}

                {/* Rating */}
                <div className="mb-3 flex items-center gap-2">
                    <div className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map((star) => (
                            <Star key={star} className={`h-3 w-3 ${star <= averageRating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`} />
                        ))}
                    </div>
                    {reviewCount > 0 && (
                        <span className="text-xs text-gray-600">
                            {averageRating.toFixed(1)} ({reviewCount} avaliações)
                        </span>
                    )}
                </div>

                {/* Summary */}
                {isFeatured ? (
                    <p className="mb-4 line-clamp-2 min-h-[2lh] text-sm text-gray-600">{summaryText || '\u00A0'}</p>
                ) : (
                    summary && <p className="mb-4 line-clamp-2 h-[2lh] text-sm text-gray-600">{summary}</p>
                )}

                {/* View Profile Button */}
                <button
                    onClick={() => onViewProfile?.()}
                    className="mt-auto w-full rounded-lg bg-blue-700 py-2 text-sm font-semibold text-white transition-colors hover:cursor-pointer hover:bg-blue-800"
                >
                    Ver perfil
                </button>
            </div>
        </div>
    );
};
